import { HubSpotSyncBehavior } from '@monetize/types/app';
import { useSaveHubSpotSyncBehavior } from '../../../../../../api/settingsService';
import {
  MCard,
  MCardBody,
  MDivider,
  MFlex,
  MText,
} from '../../../../../../components/Monetize';
import { useAsyncStatus } from '../../../../../../hooks/useAsyncStatus';
import { useFlags } from '../../../../../../services/launchDarkly';
import { SettingsToggle } from '../../../../components/SettingsToggle';

const DEAL_LINE_ITEM_SYNC_DESCRIPTION =
  'When enabled, line items on a MonetizeNow quote are pushed to the associated HubSpot Deal. Turn this off to keep HubSpot Deal line items unchanged.';
const PRODUCT_CATALOG_SYNC_DESCRIPTION =
  'When enabled, creating or updating a MonetizeNow product also creates or updates the corresponding HubSpot product.';

export const HubSpotAppSettingsSection = ({
  syncBehavior,
}: {
  syncBehavior?: HubSpotSyncBehavior;
}) => {
  const { mutateAsync: saveSyncBehavior } = useSaveHubSpotSyncBehavior({
    meta: { showErrorToast: true },
  });
  const { enableProductCatalogSyncToHubspot } = useFlags();
  const { status: syncBehaviorStatus, track: trackSyncBehavior } =
    useAsyncStatus();
  const { status: productCatalogSyncStatus, track: trackProductCatalogSync } =
    useAsyncStatus();

  const isDealLineItemSyncEnabled =
    syncBehavior?.fromMn?.dealLineItems?.enabled ?? true;
  const isProductCatalogSyncEnabled =
    syncBehavior?.fromMn?.productCatalog?.enabled ?? false;
  // Each save sends the full `fromMn` state, so overlapping saves could let an
  // older request overwrite a newer choice. Lock both toggles while either saves.
  const isSavingSyncBehavior =
    syncBehaviorStatus === 'loading' || productCatalogSyncStatus === 'loading';

  const saveFromMn = (
    track: typeof trackSyncBehavior,
    fromMn: NonNullable<HubSpotSyncBehavior['fromMn']>,
  ) =>
    track(async () => {
      await saveSyncBehavior({
        fromMn: {
          dealLineItems: { enabled: isDealLineItemSyncEnabled },
          productCatalog: { enabled: isProductCatalogSyncEnabled },
          ...fromMn,
        },
      });
    });

  const onDealLineItemSyncChange = (enabled: boolean) =>
    saveFromMn(trackSyncBehavior, { dealLineItems: { enabled } });
  const onProductCatalogSyncChange = (enabled: boolean) =>
    saveFromMn(trackProductCatalogSync, { productCatalog: { enabled } });

  return (
    <MFlex gap="4" direction="column">
      <MFlex color="tPurple.dark" direction="inherit" gap="2">
        <MText fontWeight="semibold">Sync Settings</MText>
      </MFlex>
      <MCard>
        <MCardBody>
          <SettingsToggle
            name="hubspotDealLineItemSync"
            heading="Deal Line Item Syncing"
            subheading={DEAL_LINE_ITEM_SYNC_DESCRIPTION}
            value={isDealLineItemSyncEnabled}
            asyncStatus={syncBehaviorStatus}
            isDisabled={isSavingSyncBehavior}
            onChange={onDealLineItemSyncChange}
          />
          {enableProductCatalogSyncToHubspot && (
            <>
              <MDivider my={4} />
              <SettingsToggle
                name="hubspotProductCatalogSync"
                heading="Sync Product Catalog"
                subheading={PRODUCT_CATALOG_SYNC_DESCRIPTION}
                value={isProductCatalogSyncEnabled}
                asyncStatus={productCatalogSyncStatus}
                isDisabled={isSavingSyncBehavior}
                onChange={onProductCatalogSyncChange}
              />
            </>
          )}
        </MCardBody>
      </MCard>
    </MFlex>
  );
};
